import React, { useContext, useEffect, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link, useNavigate } from "react-router-dom";
import siteConfig, { formatPrice } from "../config/siteConfig";

const initialForm = {
  email: "",
  country: "Hong Kong",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  address2: "",
  city: "",
  postalCode: "",
  phone: "",
  cardNumber: "",
  expiration: "",
  securityCode: "",
  cardName: "",
  billingSame: true,
};

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = `Checkout — ${siteConfig.brandName}`;
  }, []);

  const items = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = items.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const shipping = subtotal >= siteConfig.shipping.freeThreshold ? 0 : siteConfig.shipping.standard;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const formatCardNumber = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
    setForm((current) => ({ ...current, cardNumber: value }));
    if (errors.cardNumber) setErrors((current) => ({ ...current, cardNumber: "" }));
  };

  const formatExpiration = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
    const value = digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
    setForm((current) => ({ ...current, expiration: value }));
    if (errors.expiration) setErrors((current) => ({ ...current, expiration: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Enter a valid email address.";
    ["firstName", "lastName", "address", "city", "postalCode", "cardName"].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });
    if (form.cardNumber.replace(/\s/g, "").length !== 16) nextErrors.cardNumber = "Enter a 16-digit card number.";
    if (!/^(0[1-9]|1[0-2])\s\/\s\d{2}$/.test(form.expiration)) nextErrors.expiration = "Use MM / YY.";
    if (!/^\d{3,4}$/.test(form.securityCode)) nextErrors.securityCode = "Enter a valid security code.";
    return nextErrors;
  };

  const submitOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstField = Object.keys(nextErrors)[0];
      document.querySelector(`[name="${firstField}"]`)?.focus();
      return;
    }

    setIsSubmitting(true);
    const orderNumber = `HK${Date.now().toString().slice(-8)}`;
    window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", { state: { orderNumber, total } });
    }, 700);
  };

  if (items.length === 0) {
    return (
      <main className="checkout-empty">
        <p>Checkout</p>
        <h1>Your bag is empty</h1>
        <Link to="/shop">Return to the collection</Link>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <form className="checkout-form" onSubmit={submitOrder} noValidate>
        <section className="checkout-form__section">
          <div className="checkout-section-title">
            <span>01</span>
            <h1>Contact</h1>
          </div>
          <label className="checkout-field checkout-field--full">
            <span>Email address</span>
            <input name="email" type="email" autoComplete="email" value={form.email} onChange={updateField} aria-invalid={Boolean(errors.email)} />
            {errors.email && <small>{errors.email}</small>}
          </label>
        </section>

        <section className="checkout-form__section">
          <div className="checkout-section-title">
            <span>02</span>
            <h2>Delivery</h2>
          </div>
          <div className="checkout-fields">
            <label className="checkout-field checkout-field--full">
              <span>Country / region</span>
              <select name="country" value={form.country} onChange={updateField}>
                <option>Hong Kong</option>
                <option>Italy</option>
                <option>France</option>
                <option>Germany</option>
                <option>United Kingdom</option>
                <option>United States</option>
                <option>China</option>
                <option>Japan</option>
                <option>Australia</option>
              </select>
            </label>
            <label className="checkout-field">
              <span>First name</span>
              <input name="firstName" autoComplete="given-name" value={form.firstName} onChange={updateField} aria-invalid={Boolean(errors.firstName)} />
              {errors.firstName && <small>{errors.firstName}</small>}
            </label>
            <label className="checkout-field">
              <span>Last name</span>
              <input name="lastName" autoComplete="family-name" value={form.lastName} onChange={updateField} aria-invalid={Boolean(errors.lastName)} />
              {errors.lastName && <small>{errors.lastName}</small>}
            </label>
            <label className="checkout-field checkout-field--full">
              <span>Company (optional)</span>
              <input name="company" autoComplete="organization" value={form.company} onChange={updateField} />
            </label>
            <label className="checkout-field checkout-field--full">
              <span>Address</span>
              <input name="address" autoComplete="street-address" value={form.address} onChange={updateField} aria-invalid={Boolean(errors.address)} />
              {errors.address && <small>{errors.address}</small>}
            </label>
            <label className="checkout-field checkout-field--full">
              <span>Apartment, suite, etc. (optional)</span>
              <input name="address2" value={form.address2} onChange={updateField} />
            </label>
            <label className="checkout-field">
              <span>City</span>
              <input name="city" autoComplete="address-level2" value={form.city} onChange={updateField} aria-invalid={Boolean(errors.city)} />
              {errors.city && <small>{errors.city}</small>}
            </label>
            <label className="checkout-field">
              <span>Postal code</span>
              <input name="postalCode" autoComplete="postal-code" value={form.postalCode} onChange={updateField} aria-invalid={Boolean(errors.postalCode)} />
              {errors.postalCode && <small>{errors.postalCode}</small>}
            </label>
            <label className="checkout-field checkout-field--full">
              <span>Phone (optional)</span>
              <input name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={updateField} />
            </label>
          </div>
          <div className="shipping-method">
            <span>Tracked standard delivery</span>
            <strong>{shipping === 0 ? "Complimentary" : formatPrice(shipping)}</strong>
          </div>
        </section>

        <section className="checkout-form__section">
          <div className="checkout-section-title">
            <span>03</span>
            <h2>Payment</h2>
          </div>
          <p className="payment-note">All transactions are encrypted. This front-end demo does not transmit or store payment details.</p>
          <div className="payment-card">
            <div className="payment-card__head">
              <span>Credit card</span>
              <div aria-label="Accepted cards"><i>VISA</i><i>MC</i><i>AMEX</i></div>
            </div>
            <div className="checkout-fields">
              <label className="checkout-field checkout-field--full">
                <span>Card number</span>
                <input name="cardNumber" inputMode="numeric" autoComplete="cc-number" value={form.cardNumber} onChange={formatCardNumber} aria-invalid={Boolean(errors.cardNumber)} />
                {errors.cardNumber && <small>{errors.cardNumber}</small>}
              </label>
              <label className="checkout-field">
                <span>Expiration date</span>
                <input name="expiration" inputMode="numeric" autoComplete="cc-exp" placeholder="MM / YY" value={form.expiration} onChange={formatExpiration} aria-invalid={Boolean(errors.expiration)} />
                {errors.expiration && <small>{errors.expiration}</small>}
              </label>
              <label className="checkout-field">
                <span>Security code</span>
                <input name="securityCode" inputMode="numeric" autoComplete="cc-csc" maxLength="4" value={form.securityCode} onChange={updateField} aria-invalid={Boolean(errors.securityCode)} />
                {errors.securityCode && <small>{errors.securityCode}</small>}
              </label>
              <label className="checkout-field checkout-field--full">
                <span>Name on card</span>
                <input name="cardName" autoComplete="cc-name" value={form.cardName} onChange={updateField} aria-invalid={Boolean(errors.cardName)} />
                {errors.cardName && <small>{errors.cardName}</small>}
              </label>
            </div>
          </div>
          <label className="billing-check">
            <input name="billingSame" type="checkbox" checked={form.billingSame} onChange={updateField} />
            <span>Use shipping address as billing address</span>
          </label>
        </section>

        <button className="checkout-submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Processing…" : `Pay ${formatPrice(total)}`}
        </button>
        <p className="checkout-legal">By placing this order, you agree to our <Link to="/terms">terms</Link> and <Link to="/privacy">privacy notice</Link>.</p>
      </form>

      <aside className="checkout-summary">
        <div className="checkout-summary__inner">
          <p className="checkout-summary__label">Order summary</p>
          <div className="checkout-summary__items">
            {items.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div>
                  <img src={item.product.image} alt={item.product.name} />
                  <span>{item.quantity}</span>
                </div>
                <p>
                  <strong>{item.product.name}</strong>
                  <small>{item.color} / {item.size}</small>
                </p>
                <b>{formatPrice(item.product.price * item.quantity)}</b>
              </article>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Shipping</dt><dd>{shipping === 0 ? "Complimentary" : formatPrice(shipping)}</dd></div>
          </dl>
          <div className="checkout-summary__total"><span>Total <small>{siteConfig.currency.code}</small></span><strong>{formatPrice(total)}</strong></div>
        </div>
      </aside>
    </main>
  );
};

export default CheckOut;
